import { computed, nextTick, ref } from 'vue'
import { api } from '@/global/services/api'
import { debounce, isArray, isFunction } from '@/global/utils/utils'
import { notify } from '@/global/services/notification/notificationBus'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import TaskPathsMapPanel from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskPathsMapPanel.vue'
import { useTaskCodebooks } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskCodebooks'
import { useTaskStreetPaths } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskStreetPaths'
import { tasksScreenView } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useScreenView'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  TASKS_ENDPOINT,
  TASKS_PER_PAGE_OPTIONS,
  TASKS_TABLE_KEY,
  TASKS_TO_REQUESTS_ENDPOINT,
  TASKS_TRANSLATION_NAMESPACE as NS,
  TASKS_UI_VIEW,
  TASK_CREATE_ROUTE,
  TASK_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

const { buildConfig } = useTableService()

// How long the table waits after the last tick before it asks for the routes. Ticking
// several tasks in a row is one decision, not one per row: fetching and redrawing on
// every click made the map redraw a few thousand routes over and over, and the user
// paid for all of it. Long enough to cover a run of clicks, short enough that a single
// tick still feels immediate.
const STREETS_FETCH_DELAY = 700

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: TASKS_ENDPOINT
  }
}

/**
 * The tasks listing.
 *
 * Every row carries a checkbox and the screen-split picker is always offered, so the
 * routes of any tasks can be looked at on the map without anything being started
 * first. The listing is also where tasks are turned into incoming requests: the
 * `Convert to requests` button puts the table into a picking mode - whatever is
 * ticked is what gets converted - and the second press on it confirms the lot.
 */
export function useTable (options) {
  const {
    trans,
    router,
    openConvertDialogFn,
    closeConvertDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  // The listing endpoint reports no status of its own - only the filter offers the
  // active/inactive pair, the same one the details form edits.
  const { activeFilterItems } = useActiveStatus({ trans })
  const {
    taskTypeItems,
    taskCategoryItems,
    cleaningTypeItems
  } = useTaskCodebooks({ translate: trans })
  const { loadStreets, isFetching: isFetchingStreets } = useTaskStreetPaths()

  // Picking mode. Only the conversion itself waits on it - the checkboxes and the map
  // stand on their own, so the ticked tasks can be looked at before anything is
  // converted.
  const isConverting = ref(false)
  const shouldResetSelections = ref(false)
  const selectedTasks = ref([])
  const selectedStreets = ref([])

  // The plain pair the `also show unplanned` filter offers. 1/0 rather than true/false:
  // the listing endpoint reads its filters straight off the query string.
  const yesNoItems = computed(() => ([
    {
      id: 1,
      name: trans('base.yes')
    },
    {
      id: 0,
      name: trans('base.no')
    }
  ]))

  const selectedTaskIds = computed(() => selectedTasks.value
    .map(task => Number(task?.id))
    .filter(id => !!id)
  )

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TASKS_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onShowTaskDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: TASK_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  const onCreateNewTask = () => {
    router.push({ name: TASK_CREATE_ROUTE })
  }

  const deleteTask = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn(true)
      await api(WINTER_SERVICE_MODULE).delete(`${TASKS_ENDPOINT}/${id}`)
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onTaskDelete = (event) => {
    openInfoDialogFn({
      title: trans(`${NS}.delete_confirmation_title`),
      message: trans(`${NS}.delete_confirmation_message`),
      onConfirm: () => deleteTask(event)
    })
  }

  // Only the answer to the last ask is drawn: a selection that changed while its
  // streets were on their way has a fetch of its own behind it.
  let streetsRequestToken = 0

  const applySelectedStreets = async () => {
    const token = ++streetsRequestToken
    const streets = await loadStreets(selectedTaskIds.value)

    if (token !== streetsRequestToken) {
      return
    }

    selectedStreets.value = streets
  }

  const loadSelectedStreets = debounce(applySelectedStreets, STREETS_FETCH_DELAY)

  /**
   * Whether the loader covers the table, asked rather than told.
   *
   * The config below is a computed, so anything it reads is something it is rebuilt
   * for - and DataTable hands a rebuilt config straight to the rows, which redraws
   * every one of them. A listing showing three thousand tasks at once cannot pay that
   * to turn a spinner on and off on every tick, so the fetch is reported through a
   * function the table calls instead of through a value the config reads. The streets
   * below travel the same way, and for the same reason.
   */
  const isTableLoading = () => isFetchingStreets.value

  // A function rather than a ref: `buildConfig` deep-clones what it is given, which
  // turns a ref into a plain copy of whatever was in it at the time - the map would be
  // handed something it cannot read, and the config would be back to being rebuilt on
  // every change.
  const getSelectedStreets = () => selectedStreets.value

  /**
   * The tasks listing reports no geometry, so ticking a task off means fetching its
   * streets - the whole selection in one call, and remembered afterwards. The ticking
   * itself is applied at once; only the fetch waits, and only for rows: ticking them
   * one by one is a run of clicks, while the header checkbox settles the whole page in
   * one press and has nothing to wait for.
   */
  const onSelectionsChange = (event) => {
    selectedTasks.value = isArray(event?.data) ? event.data : []

    if (event?.allRows) {
      loadSelectedStreets.cancel()
      applySelectedStreets()

      return
    }

    loadSelectedStreets()
  }

  const stopConverting = async () => {
    isConverting.value = false
    // The chosen split is left alone: it is the user's view of the listing rather
    // than part of the picking mode.
    selectedTasks.value = []
    // Nothing is picked any more, so neither a fetch waiting to start nor one already
    // on its way has anything left to draw.
    loadSelectedStreets.cancel()
    streetsRequestToken += 1
    selectedStreets.value = []
    // DataTable clears its own selection state when it sees this turn truthy, so the
    // flag has to be lowered again - but only after the watcher it drives has run.
    shouldResetSelections.value = true
    await nextTick()
    shouldResetSelections.value = false
  }

  const onConvertToRequests = () => {
    if (!isConverting.value) {
      isConverting.value = true
      return
    }

    if (!selectedTaskIds.value.length) {
      notify(trans(`${NS}.select_at_least_one_task`))
      return
    }

    openConvertDialogFn?.({
      taskCount: selectedTaskIds.value.length,
      onConfirm: () => convertToRequests()
    })
  }

  const convertToRequests = async () => {
    try {
      await api(WINTER_SERVICE_MODULE).post(TASKS_TO_REQUESTS_ENDPOINT, {
        task_ids: selectedTaskIds.value
      })

      closeConvertDialogFn?.()
      stopConverting()
      fetchTableData()
    }
    catch (exception) {
      // The api layer has already reported what the backend complained about; the
      // picking mode stays open so the selection can be corrected.
      console.error(exception)
    }
  }

  // The listing reports every codebook as its own object, so the columns read their
  // label off `description` rather than off a flat key.
  const codebookCell = (key) => ({
    [key]: {
      custom_display_value: (data) => data?.[key]?.description || data?.[key]?.name || ''
    }
  })

  const cellsConfig = computed(() => ({
    ...codebookCell('type'),
    ...codebookCell('category'),
    ...codebookCell('surface_type'),
    ...codebookCell('vehicle_type'),
    ...codebookCell('drive_type'),
    ...codebookCell('spread_type'),
    ...codebookCell('throw_side'),
    ...codebookCell('ownership'),
    spread_quantity: {
      custom_display_value: (data) => data?.spread_quantity ?? '-'
    }
  }))

  // Only what the listing reports, and `sortable` only where it can order by it.
  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${NS}.task_name`),
      key: 'name',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.task_type`),
      key: 'type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.task_category`),
      key: 'category',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.surface_type`),
      key: 'surface_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.vehicle_type`),
      key: 'vehicle_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.drive_type`),
      key: 'drive_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.spread_type`),
      key: 'spread_type',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.spread_quantity`),
      key: 'spread_quantity',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.throw_side`),
      key: 'throw_side',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.contractor`),
      key: 'ownership',
      sortable: true,
      always_visible: true
    }
  ]))

  // Only the filters the listing endpoint actually understands.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'type_id',
        label: trans(`${NS}.task_type`),
        type: 'autocomplete',
        items: taskTypeItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      },
      {
        key: 'category_id',
        label: trans(`${NS}.task_category`),
        type: 'autocomplete',
        items: taskCategoryItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      },
      {
        key: 'cleaning_type_id',
        label: trans(`${NS}.cleaning_type`),
        type: 'autocomplete',
        items: cleaningTypeItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      },
      {
        key: 'driver_id',
        label: trans(`${NS}.driver`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'people',
          route: 'people/autocomplete'
        }
      },
      {
        key: 'is_active',
        label: trans(`${NS}.status`),
        type: 'autocomplete',
        items: activeFilterItems.value,
        autocomplete_options: {
          // A fixed pair of choices - there is nothing to look up remotely.
          allowSearch: false
        }
      },
      {
        // The listing leaves unplanned tasks out unless this is asked for, so it reads
        // as `also show them` rather than as a status of its own - picking `no` is the
        // same as not filtering at all.
        key: 'is_unplanned',
        label: trans(`${NS}.show_unplanned`),
        type: 'autocomplete',
        items: yesNoItems.value,
        autocomplete_options: {
          allowSearch: false
        }
      }
    ]
  }))

  // Show details and delete only - a task is edited on its own screen, which the
  // details action is the way into.
  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowTaskDetails),
    deleteAction(trans, onTaskDelete)
  ]))

  const screenViewComponents = computed(() => ([
    {
      component: tasksScreenView.toggle,
      // DataTable renders the grid-switcher and column-visibility icons ahead of this
      // slot and gives them no order of their own, so any negative value pulls this
      // in front of them.
      order: -1
    }
  ]))

  const additionalButtons = computed(() => ([
    {
      // Leaving the picking mode. Only offered while in it, next to the confirm.
      type: 'cancel',
      label: trans('base.cancel'),
      visible: () => isConverting.value,
      click: stopConverting
    },
    {
      // The first press opens the picking mode, the second confirms what was picked -
      // which is why it turns green once tasks can be ticked.
      type: isConverting.value ? 'green-save' : 'save',
      label: trans(`${NS}.convert_to_requests`),
      click: onConvertToRequests
    }
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.tasks')
      },
      {
        text: trans(`${NS}.overview`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: TASKS_TABLE_KEY,
      uiView: TASKS_UI_VIEW,
      apiConfig: API_CONFIG,
      // Ticking a task fetches its streets, and a whole page of them is a real wait.
      // The loader covers the table and the map beside it rather than the map alone,
      // so nothing else is clicked while the selection is still being answered.
      showLoader: isTableLoading,
      // `All` among the choices: the whole listing on one page is what makes it
      // possible to tick every task and see all of their routes at once.
      paginationConfig: {
        perPageOptions: TASKS_PER_PAGE_OPTIONS
      },
      // Off while tasks are being picked, along with the details action above: a row
      // is ticked there, and a click that navigates away would throw the selection
      // away with it.
      rowDblClick: isConverting.value ? null : onShowTaskDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      search_and_buttons_config: {
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        additional_buttons: additionalButtons.value,
        showNewBtn: !isConverting.value,
        newClick: onCreateNewTask,
        newButtonConfig: {
          label: trans(`${NS}.new_task`)
        }
      },
      rowsSelection: {
        // Always there: ticking a task is how its route gets onto the map, which is
        // worth looking at on its own - converting is only one thing done with it.
        showCheckboxes: true,
        multiple: true,
        resetSelections: shouldResetSelections.value,
        // Keyed by the task's id rather than the row index, so the map cannot
        // disagree about which task is ticked. Nothing survives a refetch - turning
        // the page, searching or filtering starts from an empty selection.
        rememberSelectionByDataProperty: 'id',
        selectionsChange: onSelectionsChange
      },
      // The screen-split picker. Always offered - it is how the map is asked for.
      filters_row_end_components: screenViewComponents.value,
      // Gone only in the map-only view, where the map is the whole screen. The
      // selection it draws is kept across the switch, so tasks ticked in the split
      // view are what the full-width map shows.
      hideTable: !tasksScreenView.isTableVisible.value,
      // The map shows the streets of every ticked task; the picker above decides
      // whether it takes room.
      sideTableComponent: {
        component: TaskPathsMapPanel,
        visible: tasksScreenView.isPanelVisible.value,
        // Asked for rather than handed over - see `getSelectedStreets`. The panel
        // reads through this, so a changed selection reaches the map without the
        // table's config, and with it every row on screen, being built again.
        streets: getSelectedStreets,
        // The routes of a whole page of tasks at once: direction arrows on top of that
        // are noise, and they are drawn per route.
        showArrows: false
      },
      globalStyle: tasksScreenView.globalStyle.value,
      tableConfig: {
        // The whole actions column steps aside while tasks are being picked: the rows
        // are there to be ticked, and both actions - opening a task and deleting one -
        // are about a single row rather than about the selection.
        displayActions: !isConverting.value,
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
